import { Controller } from '@hotwired/stimulus';

// The upload widget (docs/UPLOADS.md): pick, drop or paste a file, crop it if
// the uploader says so, send it to Fire::UploadsController with a progress
// bar, and keep the answer (Shrine's cached JSON) in a hidden field the form
// posts. The server draws every word and class; this only moves them.
//
//   pick ─► accepts? ─► decode ─► crop dialog │ shrink to BOX │ as is
//        ─► too big? ─► transport() ─► hidden field ─► upload:change
//
// With no JS the plain <input type=file> posts as it always did.
//
// Everything that listens is a Stimulus action in the markup, so Turbo
// swapping the page leaves nothing behind. What this adds by hand (the form's
// submit guard, a ResizeObserver, XHRs, the crop's object url) disconnect()
// takes back. A tile's preview url stays: Turbo's copy of the page shows it.
// ponytail: a copy Turbo drops keeps those bytes until the next full load.
const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
const fmt = (text, vars) => text.replace(/%\{(\w+)\}/g, (_, k) => vars[k] ?? '');
const EXT = { 'image/webp': 'webp', 'image/png': 'png', 'image/jpeg': 'jpg' };
const SAME = ['image/png', 'image/jpeg', 'image/webp'];
const ZOOM_MAX = 4;

export default class extends Controller {
  static targets = ['input', 'zone', 'cached', 'remove', 'list', 'proto', 'dialog', 'stage', 'photo', 'frame', 'zoom'];
  static values = {
    url: String,
    token: String,
    name: String,
    multiple: Boolean,
    crop: Array,
    shape: String,
    box: Array,
    keep: Boolean,
    format: String,
    max: Number,
    limit: Number,
    accept: Array,
    words: Object,
  };

  connect() {
    this.xhrs = new Set();
    this.holding = 0;
    this.guard = (event) => {
      if (this.holding) event.preventDefault();
    };
    this.form = this.element.closest('form');
    this.form?.addEventListener('submit', this.guard);
    // Back to a page Turbo kept mid-upload: its copy kept the form's count,
    // the held Save and a tile still uploading, of an upload that is gone.
    // The tile goes back to what it held; the count starts again from the
    // widgets on the page.
    this.element.querySelectorAll('[data-upload-tile][data-state=uploading]').forEach((tile) => this.unstick(tile));
    if (this.form) this.count(this.tally());
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.guard);
    this.xhrs.forEach((xhr) => xhr.abort());
    // An aborted request settles its own hold (send's finally), once.
    this.closed();
  }

  // ── Getting a file ────────────────────────────────────────────────────

  pick(event) {
    const files = [...event.target.files];
    // The bytes go up in our own request; the input must not post them twice.
    event.target.value = '';
    this.take(files);
  }

  over(event) {
    if (![...(event.dataTransfer?.types ?? [])].includes('Files')) return;
    event.preventDefault();
    event.currentTarget.dataset.over = '';
  }

  leave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) delete event.currentTarget.dataset.over;
  }

  drop(event) {
    event.preventDefault();
    delete event.currentTarget.dataset.over;
    this.take(event.dataTransfer.files);
  }

  paste(event) {
    const files = event.clipboardData?.files;
    if (!files?.length) return;
    event.preventDefault();
    this.take(files);
  }

  async take(files) {
    files = [...files];
    if (!this.multipleValue) files = files.slice(0, 1);
    // One at a time: only a crop dialog needs it, and only one fits on a screen.
    for (const file of files) await this.one(file);
  }

  async one(file) {
    // The last pick wins from the pick on, a refused one too: one still being
    // read when the next comes is dropped once read, one on its way is
    // stopped, and neither is ever drawn over the refusal she just saw.
    const pick = (this.picked = {});
    const stale = () => !this.multipleValue && this.picked !== pick;
    if (!this.accepts(file)) {
      const tile = this.tile();
      this.stop(tile);
      return this.refuse(tile, fmt(this.wordsValue.wrong_type, { name: file.name }), file);
    }

    // Save waits from the pick: reading and shrinking come before send's own
    // hold, and a Save between them posted the form without the picture.
    let tile;
    this.hold(1);
    try {
      const bitmap = await this.decode(file);
      let ready = file;
      let rect = null;
      if (bitmap && this.hasCropValue) {
        const cut = await this.crop(bitmap, file);
        if (!cut) return bitmap.close();
        ({ file: ready, rect = null } = cut);
      } else if (bitmap && this.hasBoxValue && file.type !== 'image/gif') {
        ready = await this.shrink(bitmap, file);
      }
      bitmap?.close();
      if (stale()) return;

      tile = this.tile();
      if (this.hasMaxValue && ready.size > this.maxValue) {
        return this.refuse(tile, fmt(this.wordsValue.too_big, { name: ready.name }), ready);
      }
      this.send(tile, ready, rect);
    } catch {
      // Whatever broke on the way (a canvas that gave nothing back), she
      // hears it: a pick never vanishes with no word.
      if (!stale()) this.refuse(tile ?? this.tile(), this.wordsValue.failed, file);
    } finally {
      this.hold(-1);
    }
  }

  // Wrong type is refused here, before a byte leaves. A file with no type
  // (some systems name none for HEIC) is the server's to judge.
  accepts(file) {
    if (!this.acceptValue.length || !file.type) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return this.acceptValue.some((rule) => {
      if (rule.startsWith('.')) return rule.slice(1) === ext;
      return rule.endsWith('/*') ? file.type.startsWith(rule.slice(0, -1)) : file.type === rule;
    });
  }

  // null when it is not a picture, or this browser cannot read it (HEIC in
  // Chrome): that file goes up as it is, with a chip for a preview.
  async decode(file) {
    if (!file.type.startsWith('image/') || !window.createImageBitmap) return null;
    try {
      return await createImageBitmap(file);
    } catch {
      return null;
    }
  }

  // Bigger than the biggest derivative: shrink it to fit, never crop it, and
  // never under 1px a side. PNG stays PNG (alpha). A GIF is never redrawn, it
  // may move. `always` redraws it even when it fits: a KEEP original goes up
  // with no EXIF.
  async shrink(bitmap, file, always = false) {
    const [bw, bh] = this.hasBoxValue ? this.boxValue : [Infinity, Infinity];
    const k = Math.min(1, bw / bitmap.width, bh / bitmap.height);
    if (k >= 1 && !always) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * k));
    canvas.height = Math.max(1, Math.round(bitmap.height * k));
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return this.encode(canvas, SAME.includes(file.type) ? file.type : 'image/webp', file.name);
  }

  // A browser with no WebP encoder (Safari) hands back a PNG, and a photo as
  // PNG is several times its size: JPEG then, the PNG only for transparency.
  async encode(canvas, type, name) {
    const blob = (type, quality) => new Promise((done) => canvas.toBlob(done, type, quality));
    let made = await blob(type, 0.92);
    if (made && made.type !== type && !this.seeThrough(canvas)) made = await blob('image/jpeg', 0.92);
    if (!made) throw new Error(`${name}: the canvas gave nothing back`);
    return new File([made], `${name.replace(/\.[^.]+$/, '')}.${EXT[made.type] ?? 'png'}`, { type: made.type });
  }

  seeThrough(canvas) {
    const { data } = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
    return false;
  }

  // ── The way a file goes up ────────────────────────────────────────────
  //
  // ONE function: a File in, Shrine's cached JSON out, progress on the bar.
  // Everything above (crop, shrink, the type and size refusal) and below
  // (preview, hidden field, the event) never learns how the bytes travel.
  //
  // Past the limit (nginx turns a body past 12 MB away) the bytes go straight
  // to the bucket (docs/UPLOADS.md § Past the cap). A 204 from presign is no
  // bucket (dev, test): the endpoint takes the file.
  //
  //   presign ─► PUT to cache/ (the bar) ─► confirm ─► the same JSON
  async transport(file, bar) {
    const ticket =
      this.limitValue && file.size > this.limitValue
        ? await this.request(
            'POST',
            `${this.urlValue}/presign`,
            this.body({ filename: file.name, size: file.size, type: file.type }),
          )
        : null;
    if (!ticket?.url) return this.shrine(await this.request('POST', this.urlValue, this.body({ file }), bar));

    await this.request(ticket.method, ticket.url, file, bar, ticket.headers);
    return this.shrine(await this.request('POST', `${this.urlValue}/confirm`, this.body({ key: ticket.key })));
  }

  body(fields) {
    const body = new FormData();
    body.append('token', this.tokenValue);
    Object.entries(fields).forEach(([name, value]) => body.append(name, value));
    return body;
  }

  // A 200 that is not Shrine's data is a login page a redirect led to.
  shrine(data) {
    return data?.id ? data : Promise.reject({});
  }

  // Our own origin gets the CSRF token; the bucket gets only the headers it
  // signed, never our token.
  request(method, url, body, bar, headers) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.xhrs.add(xhr);
      xhr.open(method, url);
      xhr.responseType = 'json';
      const sent = headers ?? {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
      };
      Object.entries(sent).forEach(([name, value]) => xhr.setRequestHeader(name, value));
      if (bar)
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) bar.value = (event.loaded / event.total) * 100;
        };
      // The refusal is the server's { errors, whole } (docs/UPLOADS.md §
      // The endpoint); anything else has no words of its own.
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response);
        reject(xhr.status === 422 || xhr.status === 429 ? (xhr.response ?? {}) : {});
      };
      xhr.onerror = () => reject({});
      xhr.onabort = () => reject(null);
      xhr.onloadend = () => this.xhrs.delete(xhr);
      xhr.send(body);
    });
  }

  // `rect` is a KEEP uploader's frame: it rides the cached JSON as
  // metadata.crop, fractions of the picture (docs/UPLOADS.md § KEEP).
  //
  // A single tile holds one upload: a pick while the last is on its way stops
  // it, and its answer, should it still come, is never kept. A failure goes
  // back to what the tile held before both.
  async send(tile, file, rect = null) {
    const going = { before: this.going?.before ?? this.snap(tile) };
    if (!this.multipleValue) {
      this.xhrs.forEach((xhr) => xhr.abort());
      this.going = going;
    }
    const { before } = going;
    const stale = () => !this.multipleValue && this.going !== going;
    const bar = tile.querySelector('progress');
    bar.value = 0;
    this.paint(tile, 'uploading', file, rect);
    const mine = tile.dataset.url;
    // What it held rides the tile, for a copy Turbo keeps of it (unstick).
    if (!this.multipleValue) tile.dataset.was = JSON.stringify(before);
    this.hold(1);
    try {
      const data = await this.transport(file, bar);
      if (stale()) return this.revoke(mine);
      if (rect) data.metadata = { ...data.metadata, crop: rect };
      const json = this.keep(tile, data);
      this.paint(tile, 'ready');
      if (before.url) this.revoke(before.url);
      this.dispatch('change', {
        detail: {
          name: file.name,
          file,
          url: tile.dataset.url,
          removed: false,
          data: json,
          tile,
        },
      });
    } catch (refusal) {
      if (stale()) return this.revoke(mine);
      // A refused tile of a list keeps its picture: it shows what was refused.
      if (!this.multipleValue) this.revoke(tile.dataset.url);
      if (!refusal) return this.discard(tile, before);
      this.restore(tile, before);
      // A predicate follows the file's name ("is too big"); a sentence of
      // ours (whole) stands alone.
      const said = (Array.isArray(refusal.errors) ? refusal.errors : []).join(' ');
      this.refuse(tile, !said ? this.wordsValue.failed : refusal.whole ? said : `${file.name} ${said}`);
    } finally {
      this.hold(-1);
      if (this.going === going) {
        this.going = null;
        delete tile.dataset.was;
      }
    }
  }

  // A single tile's upload on its way goes, and the tile goes back to what it
  // held before it (send's catch drops the answer and the hold).
  stop(tile) {
    if (this.multipleValue || !this.going) return;
    this.xhrs.forEach((xhr) => xhr.abort());
    this.restore(tile, this.going.before);
    this.going = null;
    delete tile.dataset.was;
  }

  // The cached JSON is what the form posts: one hidden field for a single
  // file, one name[] per file of a list. A new file takes a removal back.
  keep(tile, data) {
    const json = JSON.stringify(data);
    if (this.multipleValue) {
      const held = Object.assign(document.createElement('input'), {
        type: 'hidden',
        name: this.nameValue,
        value: json,
      });
      tile.querySelector('input[type=hidden]')?.remove();
      tile.append(held);
    } else {
      this.cachedTarget.value = json;
      this.flag(false);
    }
    return json;
  }

  // ── The tile: one file's preview, name, bar and error ─────────────────

  tile() {
    if (!this.multipleValue) return this.element.querySelector('[data-upload-tile]');
    const tile = this.protoTarget.content.firstElementChild.cloneNode(true);
    this.listTarget.append(tile);
    return tile;
  }

  part(tile, name) {
    return tile.querySelector(`[data-upload-${name}]`);
  }

  // What shows for each state. The server draws the same attributes for what
  // the form already holds; the look of each state is the markup's, off
  // data-state (group/tile).
  paint(tile, state, file, rect) {
    const [img, chip, empty] = ['img', 'chip', 'empty'].map((n) => this.part(tile, n));
    if (file) {
      const image = file.type.startsWith('image/');
      const url = URL.createObjectURL(file);
      tile.dataset.url = url;
      if (image) img.src = url;
      this.frame(img, rect);
      img.hidden = !image;
      chip.hidden = image;
      empty.hidden = true;
      this.part(chip, 'ext').textContent = (file.name.split('.').pop() ?? '').toUpperCase();
      this.part(tile, 'name').textContent = file.name;
      this.part(tile, 'meta').textContent = this.size(file.size);
    } else if (state === 'empty') {
      img.hidden = chip.hidden = true;
      empty.hidden = false;
      const name = this.part(tile, 'name');
      name.textContent = name.dataset.empty;
      this.part(tile, 'meta').textContent = '';
    }
    tile.dataset.state = state;
    // Whose Remove, whose bar: two files of a list sound like two files.
    const name = this.part(tile, 'name').textContent;
    this.part(tile, 'remove').setAttribute('aria-label', fmt(this.wordsValue.remove_named, { name }));
    tile.querySelector('progress').setAttribute('aria-label', fmt(this.wordsValue.uploading_named, { name }));
    this.part(tile, 'remove').hidden = !(state === 'ready' || (state === 'error' && this.multipleValue));
    tile.querySelector('progress').hidden = state !== 'uploading';
    this.part(tile, 'removed').hidden = state !== 'removed';
    if (state !== 'error') this.part(tile, 'error').hidden = true;
  }

  // A KEEP original seen through her frame. The server draws the same four
  // numbers for a held one (AppFormBuilder#upload_tile).
  frame(img, rect) {
    if (!rect) return img.removeAttribute('style');
    const [x, y, w, h] = rect;
    Object.assign(img.style, {
      position: 'absolute',
      maxWidth: 'none',
      width: `${100 / w}%`,
      height: `${100 / h}%`,
      left: `${(-100 * x) / w}%`,
      top: `${(-100 * y) / h}%`,
    });
  }

  revoke(url) {
    if (!url) return;
    URL.revokeObjectURL(url);
  }

  forget(tile) {
    this.revoke(tile.dataset.url);
    delete tile.dataset.url;
  }

  // What a failed upload puts back: the single tile is the one the person
  // already had.
  snap(tile) {
    const img = this.part(tile, 'img');
    return {
      state: tile.dataset.state,
      src: img.getAttribute('src'),
      style: img.getAttribute('style'),
      image: !img.hidden,
      ext: this.part(tile, 'ext').textContent,
      name: this.part(tile, 'name').textContent,
      meta: this.part(tile, 'meta').textContent,
      url: tile.dataset.url,
    };
  }

  restore(tile, was) {
    if (this.multipleValue) return;
    const img = this.part(tile, 'img');
    if (was.url) tile.dataset.url = was.url;
    else delete tile.dataset.url;
    if (was.src) img.src = was.src;
    else img.removeAttribute('src');
    if (was.style) img.setAttribute('style', was.style);
    else img.removeAttribute('style');
    img.hidden = !was.image;
    this.part(tile, 'chip').hidden = was.image || was.state === 'empty';
    this.part(tile, 'empty').hidden = was.state !== 'empty';
    this.part(tile, 'ext').textContent = was.ext;
    this.part(tile, 'name').textContent = was.name;
    this.part(tile, 'meta').textContent = was.meta;
    this.paint(tile, was.state);
  }

  // A tile Turbo's copy caught uploading: a list's goes, a single one goes
  // back to what it held.
  unstick(tile) {
    if (this.multipleValue) return tile.remove();
    this.restore(tile, JSON.parse(tile.dataset.was ?? '{"state":"empty"}'));
    delete tile.dataset.was;
  }

  // A list's tile that never got a file goes; a single one goes back.
  discard(tile, was) {
    if (this.multipleValue) tile.remove();
    else this.restore(tile, was);
  }

  // The error line, and for a list the tile stands as what was refused, with
  // a way to dismiss it.
  refuse(tile, message, file) {
    if (this.multipleValue) this.paint(tile, 'error', file);
    const line = this.part(tile, 'error');
    line.textContent = message;
    line.hidden = false;
  }

  // A picture this browser cannot draw (HEIC in Chrome) is a chip.
  broken(event) {
    const tile = event.target.closest('[data-upload-tile]');
    event.target.hidden = true;
    this.part(tile, 'chip').hidden = false;
    if (!this.part(tile, 'ext').textContent) {
      this.part(tile, 'ext').textContent = this.part(tile, 'name').textContent.split('.').pop().toUpperCase();
    }
  }

  size(bytes) {
    const lang = document.documentElement.lang || undefined;
    const units = ['B', 'KB', 'MB', 'GB'];
    let n = bytes;
    let i = 0;
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i += 1;
    }
    return `${new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(n)} ${units[i]}`;
  }

  // ── Remove, and take it back ──────────────────────────────────────────

  remove(event) {
    const tile = event.target.closest('[data-upload-tile]');
    // The button pressed is gone: focus goes on, never to the page.
    if (this.multipleValue) {
      const next = tile.nextElementSibling && this.part(tile.nextElementSibling, 'remove');
      this.forget(tile);
      tile.remove();
      (next && !next.hidden ? next : this.inputTarget).focus();
    } else {
      // Nothing is deleted until Save: the flag goes up, Undo brings it down.
      this.held = this.cachedTarget.value;
      this.cachedTarget.value = '';
      this.flag(true);
      this.paint(tile, 'removed');
      this.part(tile, 'undo').focus();
    }
    this.dispatch('change', {
      detail: { name: null, file: null, url: null, removed: true, data: null, tile },
    });
  }

  // remove_<attr> is posted only while it says 1.
  flag(on) {
    this.removeTarget.value = on ? '1' : '0';
    this.removeTarget.disabled = !on;
  }

  undo(event) {
    const tile = event.target.closest('[data-upload-tile]');
    this.cachedTarget.value = this.held ?? '';
    this.flag(false);
    this.paint(tile, 'ready');
    this.part(tile, 'remove').focus();
    this.dispatch('change', {
      detail: {
        name: this.part(tile, 'name').textContent,
        file: null,
        url: tile.dataset.url ?? null,
        removed: false,
        data: this.cachedTarget.value,
        tile,
      },
    });
  }

  // While a file is on its way the form cannot be sent: it would post a
  // field that is still empty. One count on the form for every widget in it.
  hold(delta) {
    this.holding += delta;
    if (!this.form || delta === 0) return;
    this.count((Number(this.form.dataset.uploading) || 0) + delta);
  }

  // What every widget of the form holds right now: the truth a cached copy
  // of the page lost.
  tally() {
    return [...this.form.querySelectorAll(`[data-controller~=${this.identifier}]`)].reduce(
      (sum, el) => sum + (this.application.getControllerForElementAndIdentifier(el, this.identifier)?.holding ?? 0),
      0,
    );
  }

  count(count) {
    this.form.dataset.uploading = count;
    this.form.querySelectorAll('[type=submit], button:not([type])').forEach((button) => {
      if (count > 0 && !button.disabled) {
        button.disabled = true;
        button.dataset.uploadHeld = '';
      } else if (count === 0 && 'uploadHeld' in button.dataset) {
        button.disabled = false;
        delete button.dataset.uploadHeld;
      }
    });
  }

  // ── The crop dialog ───────────────────────────────────────────────────
  //
  // The picture sits under a frame at the aspect of the output, starting at
  // "cover". `z` is the zoom (1 is cover), `cx, cy` the picture's point under
  // the frame's centre. Export draws that window of the picture on a canvas
  // of exactly CROP; a KEEP uploader sends the whole picture and the window.

  crop(bitmap, file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      this.job = {
        bitmap,
        file,
        url,
        resolve,
        z: 1,
        cx: bitmap.width / 2,
        cy: bitmap.height / 2,
        W: 0,
        result: null,
      };
      this.pointers = new Map();
      Object.assign(this.photoTarget.style, {
        width: `${bitmap.width}px`,
        height: `${bitmap.height}px`,
      });
      this.photoTarget.src = url;
      this.dialogTarget.showModal();
      this.resizer = new ResizeObserver(() => this.layout());
      this.resizer.observe(this.stageTarget);
      this.layout();
      this.stageTarget.focus();
    });
  }

  layout() {
    const job = this.job;
    const stage = this.stageTarget;
    if (!job || !stage.clientWidth || (stage.clientWidth === job.W && job.H)) return;
    const [cw, ch] = this.cropValue;
    const pad = 16;
    job.W = stage.clientWidth;
    job.fw = job.W - 2 * pad;
    job.fh = (job.fw * ch) / cw;
    const tall = Math.max(160, Math.min(window.innerHeight * 0.5, 420));
    if (job.fh > tall) {
      job.fh = tall;
      job.fw = (job.fh * cw) / ch;
    }
    job.H = job.fh + 2 * pad;
    stage.style.height = `${job.H}px`;
    Object.assign(this.frameTarget.style, {
      width: `${job.fw}px`,
      height: `${job.fh}px`,
      left: `${(job.W - job.fw) / 2}px`,
      top: `${pad}px`,
    });
    job.min = Math.max(job.fw / job.bitmap.width, job.fh / job.bitmap.height);
    this.place();
  }

  place() {
    const job = this.job;
    const s = job.min * job.z;
    const halfW = job.fw / (2 * s);
    const halfH = job.fh / (2 * s);
    job.cx = clamp(job.cx, halfW, job.bitmap.width - halfW);
    job.cy = clamp(job.cy, halfH, job.bitmap.height - halfH);
    const left = job.W / 2 - job.cx * s;
    const top = job.H / 2 - job.cy * s;
    this.photoTarget.style.transform = `translate(${left}px, ${top}px) scale(${s})`;
    this.zoomTarget.value = Math.round(job.z * 100);
  }

  grab(event) {
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.stageTarget.setPointerCapture(event.pointerId);
    this.span = this.pointers.size === 2 ? this.spread() : 0;
  }

  move(event) {
    const last = this.pointers?.get(event.pointerId);
    if (!last || !this.job) return;
    const job = this.job;
    if (this.pointers.size === 1) {
      const s = job.min * job.z;
      job.cx -= (event.clientX - last.x) / s;
      job.cy -= (event.clientY - last.y) / s;
    }
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      const span = this.spread();
      if (this.span) job.z = clamp((job.z * span) / this.span, 1, ZOOM_MAX);
      this.span = span;
    }
    this.place();
  }

  release(event) {
    this.pointers?.delete(event.pointerId);
    this.span = this.pointers?.size === 2 ? this.spread() : 0;
  }

  spread() {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  wheel(event) {
    if (!this.job) return;
    event.preventDefault();
    // A trackpad pinch arrives as a wheel with ctrl held, and in small steps.
    this.job.z = clamp(this.job.z * Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0015)), 1, ZOOM_MAX);
    this.place();
  }

  zoom(event) {
    this.job.z = clamp(Number(event.target.value) / 100, 1, ZOOM_MAX);
    this.place();
  }

  // Arrows move the picture the way a finger would, + and - zoom.
  keys(event) {
    const job = this.job;
    const step = (event.shiftKey ? 32 : 8) / (job.min * job.z);
    const move = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    }[event.key];
    if (move) {
      job.cx -= move[0] * step;
      job.cy -= move[1] * step;
    } else if (event.key === '+' || event.key === '=') {
      job.z = clamp(job.z * 1.1, 1, ZOOM_MAX);
    } else if (event.key === '-' || event.key === '_') {
      job.z = clamp(job.z / 1.1, 1, ZOOM_MAX);
    } else {
      return;
    }
    event.preventDefault();
    this.place();
  }

  async use() {
    const job = this.job;
    const s = job.min * job.z;
    // The window under the frame, in the picture's own pixels.
    const win = [job.cx - job.fw / (2 * s), job.cy - job.fh / (2 * s), job.fw / s, job.fh / s];
    if (this.keepValue) {
      const { width, height } = job.bitmap;
      const rect = win.map((n, i) => Math.round((n / (i % 2 ? height : width)) * 1e6) / 1e6);
      job.result = { file: await this.shrink(job.bitmap, job.file, true), rect };
    } else {
      const [cw, ch] = this.cropValue;
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(job.bitmap, ...win, 0, 0, cw, ch);
      job.result = { file: await this.encode(canvas, this.formatValue || 'image/webp', job.file.name) };
    }
    this.dialogTarget.close();
  }

  cancel() {
    this.dialogTarget.close();
  }

  backdrop(event) {
    if (event.target === this.dialogTarget) this.dialogTarget.close();
  }

  // The dialog closed, whichever way: Use, Cancel, Esc, the backdrop.
  closed() {
    const job = this.job;
    if (!job) return;
    this.job = null;
    this.resizer?.disconnect();
    if (this.dialogTarget.open) this.dialogTarget.close();
    URL.revokeObjectURL(job.url);
    job.resolve(job.result);
  }
}
